import React from 'react';

import {
  Carousel,
  CarouselContent,
  CarouselIndicator,
  CarouselItem,
} from '@wwe-portal/ui/components/carousel';
import { Skeleton } from '@wwe-portal/ui/components/skeleton';
import { useElementSize } from '@wwe-portal/ui/hooks/use-element-size';
import { cn } from '@wwe-portal/ui/lib/lib';

import type { BaseSubscriptionPlan } from '@wwe-portal/entities/subscription.type';

import { SubscriptionCard } from './subscription-card';
import { Each } from '../../../components/utils';

export interface SubscriptionCarouselProps {
  items: BaseSubscriptionPlan[];

  currentPlan: BaseSubscriptionPlan | undefined;
  showingMorePlanIds: Set<string>;
  mutatingPlanId: string | undefined;

  onSubscribe?: (plan: BaseSubscriptionPlan) => void;
  onShowMore?: (planId: string) => void;
  onHideMore?: (planId: string) => void;
}

const MAX_CARD_SIZE = 316;
const TOTAL_CAROUSEL_PADDING = 40;

export function Layout<T extends { id: string }>({
  items,
  renderItem,
  snapIndex,
  parentWidth,
  isRevamp,
}: Readonly<{
  items: T[];
  renderItem: (item: T) => React.ReactNode;
  snapIndex?: number;
  parentWidth?: number;
  isRevamp?: boolean;
}>) {
  const isCarousel =
    parentWidth != null &&
    parentWidth < MAX_CARD_SIZE * items.length + TOTAL_CAROUSEL_PADDING;

  if (items.length <= 1 || !isCarousel) {
    return (
      <div
        className={cn('flex w-full', {
          'items-center justify-center': !isRevamp,
        })}
      >
        <div
          className={cn(
            '3xl:gap-10 flex w-full max-w-[1068px] flex-row gap-4',
            {
              'align-center justify-center px-5': !isRevamp,
            }
          )}
        >
          <Each
            data={items}
            render={(item) => (
              <div
                key={item.id}
                className="flex-1"
                style={{
                  maxWidth: MAX_CARD_SIZE,
                }}
              >
                {renderItem(item)}
              </div>
            )}
          />
        </div>
      </div>
    );
  }
  return (
    <div
      className={cn('flex w-full', {
        'items-center justify-center': !isRevamp,
      })}
    >
      <Carousel
        className="w-full overflow-visible"
        opts={{ startIndex: snapIndex }}
      >
        <CarouselContent
          className={cn('ml-0', {
            'mr-5 pl-3': !isRevamp,
          })}
        >
          {items.map((item) => (
            <CarouselItem
              className="max-w-[326px] basis-[calc(100%-6px)] pl-0 last:mr-4 md:max-w-[332px] md:basis-[332px]"
              key={item.id}
            >
              <div
                className={cn('pr-[10px] md:pr-3', {
                  'pl-[10px] md:pl-3': !isRevamp,
                })}
              >
                {renderItem(item)}
              </div>
            </CarouselItem>
          ))}
        </CarouselContent>
        {!isRevamp && (
          <div className="mt-6 w-full">
            <CarouselIndicator />
          </div>
        )}
      </Carousel>
    </div>
  );
}

export function SubscriptionCarouselSkeleton() {
  const [ref, { width }] = useElementSize();
  return (
    <div className="w-full" ref={ref}>
      <Layout
        items={[{ id: '0' }, { id: '1' }]}
        parentWidth={width}
        renderItem={({ id }) => {
          return (
            <Skeleton className="h-[482px] w-full min-w-[316px]" key={id} />
          );
        }}
      />
    </div>
  );
}

export function SubscriptionCarousel({
  items,
  currentPlan,
  onSubscribe,
  mutatingPlanId,
  showingMorePlanIds,
  onShowMore,
  onHideMore,
}: Readonly<SubscriptionCarouselProps>) {
  const [ref, { width }] = useElementSize();
  const snapIndex = items.findIndex((item) => item.id === currentPlan?.id);
  return (
    <div className="w-full" ref={ref}>
      <Layout
        items={items}
        parentWidth={width}
        renderItem={(item) => {
          return (
            <SubscriptionCard
              active={item.id === currentPlan?.id}
              isDisabled={Boolean(mutatingPlanId)}
              key={item.id}
              onClickCard={() => {
                if (showingMorePlanIds.has(item.id)) onHideMore?.(item.id);
              }}
              onClickMoreInfo={() => onShowMore?.(item.id)}
              onSubscribe={() => onSubscribe?.(item)}
              plan={item}
              showMoreInfo={showingMorePlanIds.has(item.id)}
              subscribing={item.id === mutatingPlanId}
            />
          );
        }}
        snapIndex={snapIndex || 0}
      />
    </div>
  );
}
